import React, { Suspense, lazy, useState } from 'react';
import { AnimalAvatar, Icon, Modal } from '../../components';
import { useIdentity } from '../../hooks/useIdentity';
import usePresence from '../../hooks/usePresence';
import RoomPill from './RoomPill';
import BeamGlobInfo from '../beam/BeamGlobInfo';
import './MobileNav.css';

// Lazy: same rationale as DesktopSidebar — modals are gated on first
// interaction.
const PartyModal = lazy(() => import('../party/PartyModal'));
const UserModal = lazy(() => import('../user/UserModal'));
// Switch-room sheet — opened from UserModal's "Change party room" row. Lazy
// like the other mobile sheets; only loaded on first tap.
const SwitchRoomSheet = lazy(() => import('../party/SwitchRoomSheet'));
// Beam workspace — loaded on first tap of the radar button. Pulls
// AnimalAvatar / Radar / device list which are heavier than the rest of
// the nav.
const NearbyScreen = lazy(() => import('../beam/NearbyScreen'));
// Transfers sheet (artboards 18b/18c) — loaded on first tap of the Transfers
// button. Renders its own scrim/sheet (it ports the design's `.sheet.tr-sheet`
// chrome) so it isn't wrapped in <Modal> like the Nearby flow.
const TransfersSheet = lazy(() => import('../beam/TransfersSheet'));

/**
 * MobileNav — 56px top bar visible at viewport widths below 900px.
 *
 * Layout: [avatar 38×38] [wordmark + room pill] [party icon 38×38]
 *
 * Owns the open/close state for both modals. The optional `onAvatarClick`
 * / `onPartyClick` props remain for tests that want to intercept the
 * intent (e.g. assert the button forwards), but the default behavior is
 * to open the corresponding modal locally.
 */
const MobileNav = ({ onAvatarClick, onPartyClick, onBeamClick, onTransfersClick }) => {
  const { partyCode, isInGlob, avatarSeed } = useIdentity();
  const { count } = usePresence();
  const code = isInGlob ? 'GLOB' : partyCode;

  const [partyModalOpen, setPartyModalOpen] = useState(false);
  const [partyTab, setPartyTab] = useState('join');
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [switchRoomOpen, setSwitchRoomOpen] = useState(false);
  const [beamModalOpen, setBeamModalOpen] = useState(false);
  const [transfersOpen, setTransfersOpen] = useState(false);
  // GLOB intercept: tapping Beam in #GLOB opens an explainer popup with
  // throw/join CTAs instead of the (empty-because-no-mesh-in-GLOB) workspace.
  const [globInfoOpen, setGlobInfoOpen] = useState(false);

  const handleAvatar = () => {
    if (onAvatarClick) onAvatarClick();
    setUserModalOpen(true);
  };
  const handleParty = () => {
    if (onPartyClick) onPartyClick();
    setPartyTab('join');
    setPartyModalOpen(true);
  };
  const handleBeam = () => {
    if (onBeamClick) onBeamClick();
    if (isInGlob) {
      setGlobInfoOpen(true);
    } else {
      setBeamModalOpen(true);
    }
  };
  // From inside the Transfers sheet (back/scrim/Esc) → close it.
  const handleTransfersClose = () => setTransfersOpen(false);
  // Transfers is now reached from inside the Beam popover (NearbyScreen's
  // header). Only one mobile sheet is shown at a time: close Beam, open
  // Transfers.
  const handleOpenTransfersFromBeam = () => {
    if (onTransfersClick) onTransfersClick();
    setBeamModalOpen(false);
    setTransfersOpen(true);
  };
  // The sheet's "Find devices" CTA + top-right "Beam" pill open the existing
  // Nearby/Beam flow. Close the sheet first, then route through handleBeam so
  // the GLOB intercept still applies.
  const handleTransfersFindDevices = () => {
    setTransfersOpen(false);
    handleBeam();
  };

  // "Change party room" in UserModal → close it, open the Switch-room sheet.
  const handleOpenSwitchRoom = () => setSwitchRoomOpen(true);
  // Switch-room sheet's footer CTAs reuse the PartyModal flow with the right
  // tab. The sheet closes itself first, then we open the modal.
  const handleSwitchRoomJoin = () => {
    setPartyTab('join');
    setPartyModalOpen(true);
  };
  const handleSwitchRoomThrow = () => {
    setPartyTab('throw');
    setPartyModalOpen(true);
  };

  // GLOB-info CTAs reuse the PartyModal flow with the requested tab.
  const handleGlobThrow = () => {
    setGlobInfoOpen(false);
    setPartyTab('throw');
    setPartyModalOpen(true);
  };
  const handleGlobJoin = () => {
    setGlobInfoOpen(false);
    setPartyTab('join');
    setPartyModalOpen(true);
  };

  return (
    <>
      <header className="mnav" role="banner">
        <button
          type="button"
          className="mnav__avatar-btn"
          aria-label="Open profile menu"
          onClick={handleAvatar}
        >
          <AnimalAvatar seed={avatarSeed} size={36} ring="rgba(255,255,255,.22)" />
        </button>

        <div className="mnav__center">
          <span className="mnav__wordmark">glacier</span>
          <RoomPill
            code={code}
            kind={isInGlob ? 'global' : 'party'}
            size="sm"
            count={count}
          />
        </div>

        <button
          type="button"
          className="mnav__icon-btn"
          aria-label="Open Beam — nearby devices"
          onClick={handleBeam}
        >
          <Icon name="radar" size={20} />
        </button>

        <button
          type="button"
          className="mnav__icon-btn"
          aria-label="Open party menu"
          onClick={handleParty}
        >
          <Icon name="party" size={20} />
        </button>
      </header>

      <Suspense fallback={null}>
        {partyModalOpen && (
          <PartyModal
            isOpen
            onClose={() => setPartyModalOpen(false)}
            initialTab={partyTab}
          />
        )}
        {userModalOpen && (
          <UserModal
            isOpen
            onClose={() => setUserModalOpen(false)}
            onSwitchRoom={handleOpenSwitchRoom}
          />
        )}
        {switchRoomOpen && (
          <SwitchRoomSheet
            isOpen
            onClose={() => setSwitchRoomOpen(false)}
            onJoin={handleSwitchRoomJoin}
            onThrow={handleSwitchRoomThrow}
          />
        )}
        {beamModalOpen && (
          <Modal
            isOpen
            onClose={() => setBeamModalOpen(false)}
            variant="sheet"
            ariaLabel="Nearby devices"
            bare
          >
            <NearbyScreen
              selfDevice="phone"
              onSelectPeer={() => setBeamModalOpen(false)}
              onBack={() => setBeamModalOpen(false)}
              onOpenTransfers={handleOpenTransfersFromBeam}
            />
          </Modal>
        )}
        {transfersOpen && (
          <TransfersSheet
            onClose={handleTransfersClose}
            onFindDevices={handleTransfersFindDevices}
          />
        )}
        {globInfoOpen && (
          <Modal
            isOpen
            onClose={() => setGlobInfoOpen(false)}
            variant="sheet"
            title="Beam"
            ariaLabel="Beam — party-only"
          >
            <BeamGlobInfo
              onThrowParty={handleGlobThrow}
              onJoinParty={handleGlobJoin}
            />
          </Modal>
        )}
      </Suspense>
    </>
  );
};

export default MobileNav;
